import { FormProvider, useForm } from 'react-hook-form';
import { type FC, Fragment, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Save } from 'lucide-react';
import { z } from 'zod';
import { type FieldProps, renderField } from '@/components/inspector/entity-fields/form/field-components';
import { type BackendError, useFormErrorHandler } from '@/hooks/useFormErrorHandler';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { ToastHighlight } from '@/components/ui/toast-highlight';
import { error as errorLog, warn } from '@/utilities/log';
import { Separator } from '@/components/ui/separator';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { editTag } from '@/services/tags.service';
import { Button } from '@/components/ui/button';
import { useTag } from '@/hooks/tags/useTags';
import { QueryKey } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const TagFields: FC<{ tagId: string }> = ({ tagId }) => {
  const queryClient = useQueryClient();
  const tErrors = useTranslations('errors');
  const tCommon = useTranslations('common');
  const tToasts = useTranslations('toasts');
  const tValidation = useTranslations('validation');

  const { tag: fieldData, tagIsFetching, tagIsPending } = useTag(tagId, {}, null);
  const { toast } = useToast();

  const editUserWithAuth = useAuthenticatedQueryFn(editTag);

  const editTagMutation = useMutation({
    mutationFn: editUserWithAuth,
    onSuccess: (result) => {
      toast({
        title: tToasts('editTagMutation.title'),
        descriptionClassName: 'flex min-w-0 whitespace-pre',
        description: tToasts.rich('editTagMutation.description', {
          tagName: result.name,
          highlight: (chunks) => <ToastHighlight chunks={chunks} />,
        }),
      });

      void queryClient.invalidateQueries({ queryKey: [QueryKey.TagList] });

      // TODO: We need to find a better way to handle this on the frontend,
      // but for now after updating the tag, we refresh its data from the server.
      // This is necessary because our API update logic ties the "public" field
      // to the "searchable" field (if searchable is false, public is forced to false).
      // Refreshing ensures the UI displays the correct, updated tag state.
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Tag, tagId] });
    },
    onError: (err: BackendError) => handleError(err),
  });

  const isLoading = editTagMutation.isPending;
  const disabled = tagIsFetching || isLoading;

  const formSchema = z.object({
    name: z
      .string()
      .min(1, { message: tValidation('fieldRequired', { field: tValidation('fields.nameField') }) })
      .max(255, { message: tValidation('fieldMaxLength', { field: tValidation('fields.nameField'), max: 255 }) }),
    parent_tag: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .nullable()
      .optional(),
    facet: z.boolean(),
  });

  const formFields: Array<FieldProps> = useMemo(
    () => [
      {
        'name': 'Name',
        'slug': 'name',
        'type': 'string',
        'disabled': disabled,
        'value': fieldData.name ?? '',
        'data-cy': 'tag-field-name',
        'validation': formSchema.shape.name,
        'isLoading': tagIsFetching,
      },
      {
        'name': 'Parent Tag',
        'slug': 'parent_tag',
        'type': 'tag_selector',
        'disabled': disabled,
        'tagId': tagId,
        'parentTag': fieldData.parentTag ?? null,
        'autoSave': false,
        'rootOnly': true,
        'data-cy': 'tag-field-parent_tag',
        'validation': formSchema.shape.parent_tag,
        'isLoading': tagIsFetching,
      },
      {
        'name': 'Facet',
        'slug': 'facet',
        'description': 'Whether the tag is considered a facet. It must have children in order to appear as a facet.',
        'type': 'boolean',
        'disabled': disabled,
        'value': fieldData.facet,
        'data-cy': 'tag-field-facet',
        'validation': formSchema.shape.facet,
        'isLoading': tagIsFetching,
      },
    ],
    [
      disabled,
      fieldData.name,
      fieldData.parentTag,
      fieldData.facet,
      tagId,
      tagIsFetching,
      formSchema.shape.name,
      formSchema.shape.parent_tag,
      formSchema.shape.facet,
    ],
  );

  const defaultValues = {
    name: fieldData.name ?? '',
    parent_tag: fieldData.parentTag ? { value: fieldData.parentTag.id, label: fieldData.parentTag.name } : null,
    facet: fieldData.facet ?? false,
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
    values: defaultValues,
  });

  const { handleError } = useFormErrorHandler(form.setError, form.getValues);

  function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      if (typeof values === 'object' && values !== null) {
        // The parent tag is saved as part of this same request (not a separate `moveTag`
        // call), so a failure here can't leave the name saved while the parent silently
        // didn't move — one request, one result, one toast.
        editTagMutation.mutate({
          id: tagId,
          body: {
            name: values.name,
            facet: values.facet,
            searchable: true,
            public: true,
            parentTagId: values.parent_tag?.value ?? null,
          },
        });
      } else {
        warn('Invalid data for editing user');
      }
    } catch (err) {
      errorLog('Action failed:', err);
      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: tErrors('toastDefaultDescription'),
      });
    }
  }

  return tagIsPending ? (
    <div className="flex flex-col space-y-6 p-4">
      {Array.from(Array(3)).map((_, index) => (
        <Fragment key={`skeleton-${index}`}>
          <div className="flex flex-col space-y-3">
            <Skeleton className={cn('h-5 w-1/3', { 'w-2/5': index === 0 })} />
            <Skeleton className="h-8 w-full" />
            <Skeleton className={cn('h-5 w-1/3', { 'w-2/3': index === 1 })} />
          </div>
          {index !== 2 && <Separator className="block" />}
        </Fragment>
      ))}
    </div>
  ) : (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="relative flex flex-col space-y-6 p-4">
        {formFields.map((field) => renderField(form, field))}

        <Button
          disabled={disabled || !form.formState.isDirty}
          type="submit"
          variant="navigation-action-primary"
          className="w-full justify-center focus-visible:ring-offset-ui-panel"
          data-cy="tag-save-button"
          iconClassName={cn('icon-4!', { 'animate-spin': isLoading })}
          icon={isLoading ? Loader2 : Save}
        >
          {isLoading ? tCommon('saving') : tCommon('save')}
        </Button>
      </form>
    </FormProvider>
  );
};
